import { DataTable } from '@attentive-platform/stem-ui';
import React, { useMemo, useCallback, useRef } from 'react';
import { TradeNameCell, WorkflowCell, ActionCell } from './cell';
import { useTradeData } from '../../Hooks/useTradeData';
import { getTradeId } from '../../Utils/HelperFunctions';

export const TradeTable = ({
  data,
  onDataChange,
  loading = false,
  noDataText = 'No trades found',
  disableAdd = false
}) => {
  const { loading: tradeLoading, tradesData, workflowOptions } = useTradeData();

  const selectedTradesSetRef = useRef(new Set());

  const selectedTrades = useMemo(() => {
    const trades = data.reduce((acc, item) => {
      if (item.trade !== '') {
        acc.push(item.trade);
      }
      return acc;
    }, []);

    // Update the Set reference
    selectedTradesSetRef.current = new Set(trades);
    return trades;
  }, [data]);

  const isOptionDisabled = useCallback(option => selectedTradesSetRef.current.has(option.value), []);

  const onAddTrade = useCallback(() => {
    onDataChange(prevData => [...prevData, { id: getTradeId(), trade: '', workflow: [] }]);
  }, [onDataChange]);

  const onDeleteTrade = useCallback(
    id => {
      onDataChange(prevData => prevData.filter(item => item.id !== id));
    },
    [onDataChange]
  );

  const onTradeChange = useCallback(
    (id, trade) => {
      onDataChange(prevData => prevData.map(item => (item.id === id ? { ...item, trade: trade } : item)));
    },
    [onDataChange]
  );

  const onWorkflowChange = useCallback(
    (id, workflow) => {
      onDataChange(prevData => prevData.map(item => (item.id === id ? { ...item, workflow: workflow } : item)));
    },
    [onDataChange]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Trade',
        accessor: 'trade',
        width: '35%',
        Cell: ({ row }) => (
          <TradeNameCell
            options={tradesData}
            id={row.original.id}
            trade={row.original.trade}
            onTradeChange={onTradeChange}
            isOptionDisabled={isOptionDisabled}
          />
        )
      },
      {
        Header: 'Workflow',
        accessor: 'workflow',
        width: '45%',
        Cell: ({ row }) => (
          <WorkflowCell
            options={workflowOptions}
            id={row.original.id}
            workflows={row.original.workflow}
            onWorkflowChange={onWorkflowChange}
          />
        )
      },
      {
        Header: 'Action',
        accessor: 'action',
        width: '20%',
        align: 'center',
        Cell: ({ row }) => <ActionCell id={row.original.id} onDeleteTrade={onDeleteTrade} />
      }
    ],
    [onDeleteTrade, onTradeChange, onWorkflowChange, tradesData, workflowOptions, isOptionDisabled]
  );

  return (
    <DataTable
      isLoading={loading || tradeLoading}
      createRowProps={{
        showCreateRow: true,
        createRowBtnText: 'Add Trade',
        handleClickCreateBtn: onAddTrade,
        disableCreate:
          loading ||
          tradeLoading ||
          selectedTrades.length >= tradesData.length ||
          tradesData.length === data.length ||
          disableAdd
      }}
      columns={columns}
      data={data}
      options={{ showFooter: false, stickyHeader: true }}
      paginationProps={{ hidePagination: true }}
      allowBulkSelect={false}
      rowProps={{ isSelectable: () => false }}
      color='primary'
      noDataText={noDataText}
    />
  );
};
